import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, Key, Trash, MagnifyingGlass, ShieldCheck } from "@phosphor-icons/react";

const err = (e) => e.response?.data?.detail || e.message;

export function AdminUsersPanel() {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState("");
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookup, setLookup] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data.items)).catch(() => {});
  useEffect(() => { load(); api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Admin created — ${form.email}`, { description: "Share the password with them privately; they can change it here later." });
      setForm({ email: "", name: "", password: "" });
      load();
    } catch (e) { toast.error("Couldn't create admin", { description: err(e) }); }
    finally { setBusy(false); }
  };

  const setPassword = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${pwFor.id}/password`, { password: pw });
      toast.success(`Password updated for ${pwFor.email}`, { description: "Any sign-in lockout on this account was cleared too." });
      setPwFor(null); setPw("");
    } catch (e) { toast.error("Couldn't set password", { description: err(e) }); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from the console? They will no longer be able to sign in.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success(`${u.email} removed`); load(); }
    catch (e) { toast.error("Couldn't remove", { description: err(e) }); }
  };

  const doLookup = async () => {
    try { const { data } = await api.get("/admin/users/lookup", { params: { email: lookupEmail } }); setLookup(data); }
    catch (e) { toast.error("Lookup failed", { description: err(e) }); }
  };

  const convert = async (email) => {
    if (!window.confirm(`Make ${email} a console admin? Their member-app account is removed and any cover linked to it is unlinked.`)) return;
    try {
      await api.post("/admin/users/convert", { email });
      toast.success(`${email} is now an admin`, { description: "Set a password for them below, then they sign in at /login." });
      setLookup(null); setLookupEmail(""); load();
    } catch (e) { toast.error("Couldn't convert", { description: err(e) }); }
  };

  const roleLabel = { admin: "Admin", client: "Client portal", crew: "Responder crew" };

  return (
    <div className="border border-zinc-700/60 p-5 mt-6" data-testid="admin-users-panel">
      <div className="flex items-center gap-2 mb-1">
        <UsersThree size={20} weight="duotone" className="text-zinc-300" />
        <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Console users</h2>
      </div>
      <p className="text-xs text-zinc-400 mb-4">Who can sign in to this console. Admins see everything; client-portal and crew users are created from their own pages.</p>

      <div className="divide-y divide-zinc-800" data-testid="admin-users-list">
        {users.map((u) => (
          <div key={u.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`admin-user-${u.email}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold text-sm">{u.name || u.email} {me?.id === u.id && <span className="text-[10px] text-zinc-500 font-mono-data ml-1">YOU</span>}</div>
              <div className="text-xs text-zinc-400 font-mono-data">{u.email}</div>
            </div>
            <span className={`text-[10px] font-mono-data uppercase tracking-widest px-2 py-1 border ${u.role === "admin" ? "border-green-500/40 text-green-400" : "border-zinc-600 text-zinc-400"}`}>{roleLabel[u.role] || u.role}</span>
            <button onClick={() => { setPwFor(u); setPw(""); }} data-testid={`admin-user-pw-${u.email}`}
              className="btn-tactical bg-zinc-700 hover:bg-zinc-600 text-white text-xs inline-flex items-center gap-1"><Key size={13} /> Set password</button>
            {u.role === "admin" && me?.id !== u.id && (
              <button onClick={() => remove(u)} data-testid={`admin-user-del-${u.email}`}
                className="btn-tactical bg-red-900/60 hover:bg-red-800 text-white text-xs inline-flex items-center gap-1"><Trash size={13} /> Remove</button>
            )}
          </div>
        ))}
      </div>

      {pwFor && (
        <div className="mt-4 border border-yellow-500/40 bg-yellow-500/10 p-4" data-testid="admin-pw-box">
          <div className="text-sm font-semibold">New password for {pwFor.email}</div>
          <div className="flex gap-2 mt-2">
            <input className="tactical-input font-mono-data text-xs flex-1" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="admin-pw-input" />
            <button onClick={setPassword} disabled={busy || pw.length < 8} className="btn-tactical bg-green-600 hover:bg-green-500 text-white disabled:opacity-40" data-testid="admin-pw-save">Save</button>
            <button onClick={() => setPwFor(null)} className="btn-tactical bg-zinc-700 text-white" data-testid="admin-pw-cancel">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        <div className="border border-zinc-700/60 p-4" data-testid="admin-create-box">
          <div className="text-sm font-semibold flex items-center gap-2"><ShieldCheck size={16} /> Add an admin</div>
          <input className="tactical-input mt-3 text-xs" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-create-name" />
          <input className="tactical-input mt-2 text-xs font-mono-data" placeholder="Email (their sign-in)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-create-email" />
          <input className="tactical-input mt-2 text-xs font-mono-data" placeholder="Temporary password (8+ characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-create-password" />
          <button onClick={create} disabled={busy || !form.email || form.password.length < 8} data-testid="admin-create-submit"
            className="btn-tactical mt-3 bg-green-600 hover:bg-green-500 text-white disabled:opacity-40">Create admin</button>
        </div>

        <div className="border border-zinc-700/60 p-4" data-testid="admin-lookup-box">
          <div className="text-sm font-semibold flex items-center gap-2"><MagnifyingGlass size={16} /> Can't sign in? Look up an email</div>
          <p className="text-[11px] text-zinc-500 mt-1">Shows whether the email belongs to a member-app account — those can't open the console until you make them an admin.</p>
          <div className="flex gap-2 mt-3">
            <input className="tactical-input text-xs font-mono-data flex-1" placeholder="email@…" value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)} data-testid="admin-lookup-email" />
            <button onClick={doLookup} disabled={!lookupEmail} className="btn-tactical bg-zinc-700 hover:bg-zinc-600 text-white disabled:opacity-40" data-testid="admin-lookup-submit">Check</button>
          </div>
          {lookup && (
            <div className="mt-3 text-xs" data-testid="admin-lookup-result">
              {!lookup.found ? (
                <div className="text-zinc-300">No account exists for that email — create them as an admin on the left.</div>
              ) : (
                <>
                  <div className="text-zinc-200"><b>{lookup.user.name || lookup.user.email}</b> · role <span className="font-mono-data">{lookup.user.role}</span>
                    {lookup.contacts?.length ? ` · linked to ${lookup.contacts.length} cover record(s)` : ""}</div>
                  {lookup.user.role === "consumer" && (
                    <button onClick={() => convert(lookup.user.email)} data-testid="admin-lookup-convert"
                      className="btn-tactical mt-2 bg-[#007AFF] hover:bg-[#005ecb] text-white">Make admin (removes member account)</button>
                  )}
                  {lookup.user.role !== "consumer" && <div className="text-zinc-400 mt-1">Already a console user — set a password above if they're locked out.</div>}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
